import {
  useController,
  Control,
  FieldValues,
  RegisterOptions,
  Path,
} from "react-hook-form";
import { ReactNode, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

interface InputProps<T extends FieldValues> {
  name: Path<T>;
  control?: Control<T>;
  label?: string;
  placeholder?: string;
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    "setValueAs" | "disabled" | "valueAsNumber" | "valueAsDate"
  >;
  type?: "text" | "number" | "email" | "password" | "checkbox";
  disabled?: boolean;
  defaultValue?: any;
  icon?: ReactNode; // left-side icon
  suffix?: ReactNode; // right-side icon (e.g., eye toggle)
  passwordToggle?: boolean; // special prop to enable password eye toggle
  autoComplete?: string;
}

const IconInput = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  rules,
  type = "text",
  disabled = false,
  defaultValue = "",
  icon,
  suffix,
  passwordToggle = false,
  autoComplete = "off",
}: InputProps<T>) => {
  const {
    field: { onChange, onBlur, value = defaultValue, ref },
    fieldState: { error },
  } = useController<T>({
    name,
    control,
    rules,
    defaultValue,
  });

  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (type === "number") {
      const sanitizedValue =
        inputValue === "" ? "" : inputValue.replace(/[^0-9]/g, "");
      onChange(sanitizedValue);
    } else {
      onChange(inputValue);
    }
  };

  const finalType =
    passwordToggle && type === "password"
      ? showPassword
        ? "text"
        : "password"
      : type;

  return (
    <div className="mb-4">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {rules?.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        {/* Left Icon */}
        {icon && (
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
            {icon}
          </span>
        )}

        <input
          type={finalType}
          className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200
            ${error ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300"}
            ${disabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : "bg-white"}
            ${icon ? "pl-10" : ""}
            ${suffix || passwordToggle ? "pr-10" : ""}
          `}
          value={(value as string | number) || ""}
          onChange={handleChange}
          onBlur={onBlur}
          ref={ref}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
        />

        {/* Right Icon (suffix or password toggle) */}
        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 cursor-pointer">
          {passwordToggle && type === "password" ? (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="focus:outline-none"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          ) : (
            suffix
          )}
        </span>
      </div>

      {error && (
        <div className="text-red-500 text-sm mt-1">{error.message}</div>
      )}
    </div>
  );
};

export default IconInput;
